import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { ISelectOption } from '../../types';
import FieldErrorMessage from './FieldErrorMessage';

export interface SelectImageOption extends ISelectOption {
  image: string;
  subLabel?: string;
}

export interface ControlledSingleSelectCardInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName> {
  options: SelectImageOption[];
}

const ControlledSingleSelectCardInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  name,
  shouldUnregister = true,
  ...controllerProps
}: ControlledSingleSelectCardInputProps<TFieldValues, TName>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    name,
    ...controllerProps,
    shouldUnregister,
  });

  return (
    <div>
      <div className='flex flex-row flex-wrap'>
        {(options || []).map((option, i) => {
          const isSelected = option.value === value;

          return (
            <button
              key={option.value}
              className='w-full mb-4'
              onClick={() => {
                if (!isSelected) {
                  onChange(option.value);
                }
              }}
              disabled={option.disabled}
              data-testid={option.label}
            >
              <div
                data-testid={`selected-option-${i}`}
                className={classNames([
                  'flex flex-col justify-center items-center rounded-xl py-6 px-4 relative',
                  isSelected
                    ? 'border-2 border-primary-skyblue bg-primary-skyblue bg-opacity-10 shadow-lg shadow-gray-300'
                    : 'bg-transparent border border-primary-navy',
                ])}
              >
                {isSelected && (
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    fontSize={24}
                    className='absolute top-2 right-2 text-primary-skyblue'
                  />
                )}
                <img
                  src={option.image}
                  alt='icon'
                  className='h-10 w-10 object-contain'
                  data-testid='select-image'
                />
                <p
                  className={classNames([
                    'font-inter-medium text-lg mt-2 text-center text-black',
                  ])}
                >
                  {option.label}
                </p>
                <p
                  className={classNames([
                    'font-inter-light w-3/5 text-sm mt-2 text-center text-black',
                  ])}
                >
                  {option.subLabel}
                </p>
              </div>
            </button>
          );
        })}
      </div>
      <FieldErrorMessage message={error?.message} />
    </div>
  );
};

export default ControlledSingleSelectCardInput;
